/**
 * 🚫 Access Denied Screen
 * 
 * Displays specific error messages when users are denied platform access
 * due to organization status, user status, or other security reasons.
 */

import React from 'react';
import { 
  Result, 
  Button, 
  Typography, 
  Card, 
  Space, 
  Alert,
  Divider 
} from 'antd';
import { 
  ExclamationCircleOutlined, 
  LockOutlined, 
  TeamOutlined,
  ContactsOutlined,
  ReloadOutlined,
  LogoutOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

const AccessDeniedScreen = ({ accessDenied, onRetry, onLogout }) => {
  const getResultConfig = () => {
    switch (accessDenied?.reason) {
      case 'ORGANIZATION_NOT_FOUND':
        return {
          status: 'error',
          icon: <TeamOutlined style={{ color: '#ff4d4f' }} />,
          title: 'Organization Not Found',
          subTitle: 'Your organization no longer exists in our system.',
          extra: [
            <Button key="support" type="primary" icon={<ContactsOutlined />}>
              Contact Support
            </Button>,
            <Button key="logout" onClick={onLogout} icon={<LogoutOutlined />}>
              Sign Out
            </Button>
          ]
        };

      case 'ORGANIZATION_DELETED':
        return {
          status: 'error',
          icon: <TeamOutlined style={{ color: '#ff4d4f' }} />,
          title: 'Organization Deleted',
          subTitle: 'Your organization has been permanently deleted.',
          extra: [
            <Button key="support" type="primary" icon={<ContactsOutlined />}>
              Contact Support
            </Button>,
            <Button key="logout" onClick={onLogout} icon={<LogoutOutlined />}>
              Sign Out
            </Button>
          ]
        };

      case 'ORGANIZATION_INACTIVE':
        return {
          status: 'warning',
          icon: <TeamOutlined style={{ color: '#faad14' }} />,
          title: 'Organization Deactivated',
          subTitle: 'Your organization has been temporarily deactivated.',
          extra: [
            <Button key="contact" type="primary" icon={<ContactsOutlined />}>
              Contact Administrator
            </Button>,
            <Button key="retry" onClick={onRetry} icon={<ReloadOutlined />}>
              Check Again
            </Button>,
            <Button key="logout" onClick={onLogout} icon={<LogoutOutlined />}>
              Sign Out
            </Button>
          ]
        };

      case 'ORGANIZATION_SUSPENDED':
        return {
          status: 'error',
          icon: <LockOutlined style={{ color: '#ff4d4f' }} />,
          title: 'Organization Suspended',
          subTitle: 'Your organization has been suspended due to policy violations.',
          extra: [
            <Button key="support" type="primary" icon={<ContactsOutlined />}>
              Contact Support
            </Button>,
            <Button key="logout" onClick={onLogout} icon={<LogoutOutlined />}>
              Sign Out
            </Button>
          ]
        };

      case 'USER_DEACTIVATED':
        return {
          status: 'error',
          icon: <LockOutlined style={{ color: '#ff4d4f' }} />,
          title: 'Account Deactivated',
          subTitle: 'Your user account has been deactivated.',
          extra: [
            <Button key="contact" type="primary" icon={<ContactsOutlined />}>
              Contact Administrator
            </Button>,
            <Button key="logout" onClick={onLogout} icon={<LogoutOutlined />}>
              Sign Out
            </Button>
          ]
        };

      case 'USER_SUSPENDED':
        return {
          status: 'error',
          icon: <LockOutlined style={{ color: '#ff4d4f' }} />,
          title: 'Account Suspended',
          subTitle: 'Your user account has been suspended.',
          extra: [
            <Button key="support" type="primary" icon={<ContactsOutlined />}>
              Contact Support
            </Button>,
            <Button key="logout" onClick={onLogout} icon={<LogoutOutlined />}>
              Sign Out
            </Button>
          ]
        };

      case 'USER_NOT_FOUND':
        return {
          status: 'error',
          icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
          title: 'Account Not Found',
          subTitle: 'Your user account was not found in our system.',
          extra: [
            <Button key="support" type="primary" icon={<ContactsOutlined />}>
              Contact Support
            </Button>,
            <Button key="logout" onClick={onLogout} icon={<LogoutOutlined />}>
              Sign Out
            </Button>
          ]
        };

      case 'INVALID_USER_DATA':
        return {
          status: 'error',
          icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
          title: 'Account Configuration Error',
          subTitle: 'Your account is not properly configured.',
          extra: [
            <Button key="support" type="primary" icon={<ContactsOutlined />}>
              Contact Support
            </Button>,
            <Button key="logout" onClick={onLogout} icon={<LogoutOutlined />}>
              Sign Out
            </Button>
          ]
        };

      case 'VALIDATION_ERROR':
      case 'USER_VALIDATION_ERROR':
      case 'COMPLETE_VALIDATION_ERROR':
        return {
          status: 'warning',
          icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
          title: 'Temporary Access Issue',
          subTitle: 'Unable to verify your access permissions at this time.',
          extra: [
            <Button key="retry" type="primary" onClick={onRetry} icon={<ReloadOutlined />}>
              Try Again
            </Button>,
            <Button key="logout" onClick={onLogout} icon={<LogoutOutlined />}>
              Sign Out
            </Button>
          ]
        };

      default:
        return {
          status: 'error',
          icon: <LockOutlined style={{ color: '#ff4d4f' }} />,
          title: 'Access Denied',
          subTitle: 'You do not have permission to access this platform.',
          extra: [
            <Button key="support" type="primary" icon={<ContactsOutlined />}>
              Contact Support
            </Button>,
            <Button key="logout" onClick={onLogout} icon={<LogoutOutlined />}>
              Sign Out
            </Button>
          ]
        };
    }
  };

  const resultConfig = getResultConfig();

  const getDetailedExplanation = () => {
    switch (accessDenied?.reason) {
      case 'ORGANIZATION_NOT_FOUND':
      case 'ORGANIZATION_DELETED':
        return (
          <Alert
            type="info"
            message="What does this mean?"
            description={
              <div>
                <Paragraph>
                  Your organization has been removed from our platform. This could happen for several reasons:
                </Paragraph>
                <ul>
                  <li>Your subscription has expired</li>
                  <li>The organization was deleted by an administrator</li>
                  <li>Administrative restructuring</li>
                </ul>
                <Paragraph>
                  Please contact our support team to understand your specific situation and explore options for restoring access.
                </Paragraph>
              </div>
            }
            showIcon
          />
        );

      case 'ORGANIZATION_INACTIVE':
        return (
          <Alert
            type="warning"
            message="Temporary Deactivation"
            description={
              <div>
                <Paragraph>
                  Your organization has been temporarily deactivated. This is usually temporary and can be resolved by:
                </Paragraph>
                <ul>
                  <li>Contacting your organization administrator</li>
                  <li>Resolving any outstanding billing issues</li>
                  <li>Completing required compliance steps</li>
                </ul>
                <Paragraph>
                  Once the issue is resolved, access will be automatically restored.
                </Paragraph>
              </div>
            }
            showIcon
          />
        );

      case 'USER_DEACTIVATED':
        return (
          <Alert
            type="warning"
            message="Account Deactivated"
            description={
              <div>
                <Paragraph>
                  Your user account has been deactivated by an administrator. This could be due to:
                </Paragraph>
                <ul>
                  <li>Role changes within your organization</li>
                  <li>Security policy compliance</li>
                  <li>Administrative request</li>
                </ul>
                <Paragraph>
                  Contact your organization administrator to request account reactivation.
                </Paragraph>
              </div>
            }
            showIcon
          />
        );

      case 'VALIDATION_ERROR':
        return (
          <Alert
            type="info"
            message="Temporary Technical Issue"
            description={
              <Paragraph>
                We're experiencing a temporary issue verifying your access permissions. 
                This is usually resolved quickly. Please try again in a few moments.
              </Paragraph>
            }
            showIcon
          />
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <Card 
        style={{ 
          maxWidth: 600, 
          width: '100%',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
        }}
      >
        <Result
          status={resultConfig.status}
          icon={resultConfig.icon}
          title={<Title level={3}>{resultConfig.title}</Title>}
          subTitle={
            <div>
              <Text>{resultConfig.subTitle}</Text>
              {accessDenied?.message && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">{accessDenied.message}</Text>
                </div>
              )}
            </div>
          }
        />

        {getDetailedExplanation() && (
          <>
            <Divider />
            {getDetailedExplanation()}
          </>
        )}

        <Divider />

        <div style={{ textAlign: 'center' }}>
          <Space wrap>
            {resultConfig.extra}
          </Space>
        </div>

        <Divider />

        <div style={{ textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Access Denied • Crisis Compass Platform
            <br />
            Error Code: {accessDenied?.reason || 'UNKNOWN'}
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default AccessDeniedScreen;
